// Navigation des React-SPA-Headers.
// Items, die innerhalb der React-Welt liegen, bekommen `internal: true` und werden
// per <Link> ohne Reload geöffnet. Alles andere zeigt auf die klassische App.

import type { NavBadgeKey } from "@/lib/navBadges";

export interface NavItem {
  key: string;
  label: string;
  /** Ziel: relativer Pfad. Bei `internal: true` Router-Pfad ohne URL-Präfix. */
  href: string;
  /** Innerhalb der React-SPA → React-Router benutzen. */
  internal?: boolean;
  /** Fallback-href für die ACL-Prüfung (Pfad in der klassischen Welt). */
  aclHref?: string;
  /** Offene-Aufgaben-Badges wie in `static/navigation.js`. */
  badgeKeys?: NavBadgeKey[];
  /** Mehrere Badge-Zähler zu einem Badge addieren (z. B. Home für GF). */
  badgeSumKeys?: NavBadgeKey[];
  /** Zusätzliche Pfad-Präfixe für aktiven Nav-Zustand (Hub-Seiten). */
  activePrefixes?: string[];
}

export const MAIN_NAV: NavItem[] = [
  {
    key: "home",
    label: "Home",
    href: "/",
    internal: true,
    aclHref: "/",
    badgeSumKeys: [
      "project-completions-open",
      "billing-questions",
      "dokument-billing-questions",
      "mentions-unread",
      "kontakte-ohne-nummer",
    ],
  },
  // Projekte liegen jetzt in der React-Welt unter /react/projekte.
  // Für die ACL-Prüfung weiter den klassischen Pfad heranziehen (Server kennt
  // /projekte nicht, nur /auftraege.html).
  {
    key: "orders",
    label: "Projekte",
    href: "/projekte",
    internal: true,
    aclHref: "/auftraege.html",
  },
  {
    key: "tagesgeschaeft",
    label: "Tagesgeschäft",
    href: "/tagesgeschaeft",
    internal: true,
    aclHref: "/tagesgeschaeft.html",
  },
  {
    key: "support",
    label: "Support",
    href: "/support",
    internal: true,
    aclHref: "/support.html",
    badgeKeys: ["support-eingang", "support-action-required"],
  },
  {
    key: "schwarzes-brett",
    label: "Schwarzes Brett",
    href: "/schwarzes-brett",
    internal: true,
    // Für alle angemeldeten Nutzer sichtbar (Inhalte werden in SharePoint gepflegt).
    aclHref: "/",
  },
  {
    key: "dokumente",
    label: "Dokumente",
    href: "/dokumente",
    internal: true,
    aclHref: "/dokumente.html",
    activePrefixes: [
      "/dokumente",
      "/dokumente/buchhaltung",
      "/dokumente/rechnungen",
      "/dokumente/nachweise",
      "/dokumente/angebotswesen",
      "/dokumente/angebote",
      "/dokumente/angebotsanfragen",
      "/dokumente/stundennachweise",
      "/dokumente/fertigstellungsmeldungen",
      "/dokumente/lieferscheine",
    ],
    badgeKeys: ["mahnwesen-faellig", "lieferschein-billing-unread", "lieferschein-billing-questions"],
  },
  {
    key: "kontakte",
    label: "Kontakte",
    href: "/kontakte",
    internal: true,
    aclHref: "/kontakte.html",
    activePrefixes: ["/kontakte"],
  },
  {
    key: "angebotsanfragen",
    label: "Angebote",
    href: "/angebotsanfragen",
    internal: true,
    aclHref: "/angebotsanfragen.html",
    badgeKeys: ["angebote-open"],
  },
  // Admin liegt komplett in der React-Welt
  { key: "admin", label: "Admin", href: "/admin", internal: true, aclHref: "/admin/" },
  {
    key: "montageplanung",
    label: "Montageplanung",
    href: "/montageplanung",
    internal: true,
    // Für alle angemeldeten Nutzer sichtbar (wie Home); Schreibrechte werden
    // serverseitig auf Büro/App-Admins beschränkt.
    aclHref: "/",
    badgeKeys: ["montageplanung-feinplanung-requests", "montageplanung-monteur-proposals"],
  },
  {
    key: "wartungen",
    label: "Wartungen",
    href: "/wartungen",
    internal: true,
    aclHref: "/wartungen/",
  },
  {
    key: "timesheets",
    label: "Stundennachweise",
    href: "/stundennachweise",
    internal: true,
    aclHref: "/stundennachweise",
    badgeKeys: ["billing-unread", "billing-assigned", "billing-answered-unread"],
  },
  {
    key: "reports",
    label: "Auswertungen",
    href: "/auswertungen",
    internal: true,
    aclHref: "/auswertungen.html",
    activePrefixes: [
      "/auswertungen",
    ],
  },
];

/** Resolve the URL the browser should actually open. */
export function resolveHref(item: NavItem, newDesignEnabled = true): string {
  if (item.internal && newDesignEnabled) {
    return item.href.startsWith("/") ? item.href : `/${item.href}`;
  }
  if (item.internal && item.aclHref) {
    return item.aclHref.startsWith("/alt/") ? item.aclHref : `/alt${item.aclHref}`;
  }
  return item.href;
}

/** Legacy-Ziel für interne Nav-Items, wenn der Design-Vorschau-Schalter aus ist. */
export function legacyHrefFor(item: NavItem): string {
  const href = item.aclHref ?? item.href;
  if (href.startsWith("/alt/") || href === "/alt/") return href;
  if (href === "/") return "/alt/";
  return href.startsWith("/") ? `/alt${href}` : `/alt/${href}`;
}
